const AZURE_API_BASE = 'https://rhythm-game-dev-gateway.azurewebsites.net';
const R2_BASE_URL = 'https://pub-7f2c18f1fc224fdf883584c598fdd535.r2.dev';

// Use proxy in development to avoid CORS issues
const getApiUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return '/api-proxy/api/Announcement';
  }
  return `${AZURE_API_BASE}/api/Announcement`;
};

const ANNOUNCEMENT_API_URL = getApiUrl();

/**
 * Gets the proxy URL for R2 content in development
 * @param {string} url - The original R2 URL
 * @returns {string} - Proxied URL in dev, original in production
 */
function getProxiedUrl(url) {
  if (process.env.NODE_ENV === 'development' && url.startsWith(R2_BASE_URL)) {
    const path = url.replace(R2_BASE_URL, '');
    return `/r2-proxy${path}`;
  }
  return url;
}

/**
 * Extracts article data from HTML content and returns title.
 * Content URL is provided separately for iframe embedding.
 *
 * @param {Document|Element} container - DOM element or document to search for articles
 * @returns {Array<{title: string}>}
 */
export function extractArticleData(container = document) {
  const articles = container.querySelectorAll('article');
  const result = [];

  articles.forEach((article) => {
    // Extract title from header > h1.page-title
    const titleElement = article.querySelector('header .page-title');
    const title = titleElement ? titleElement.textContent.trim() : '';

    result.push({
      title
    });
  });

  return result;
}

/**
 * Fetches HTML from a single URL and extracts article data
 * Returns the URL as content for iframe embedding
 *
 * @param {string} url - URL to fetch HTML from
 * @returns {Promise<{title: string, content: string}|null>}
 */
async function fetchSingleArticle(url) {
  try {
    const fetchUrl = getProxiedUrl(url);
    const response = await fetch(fetchUrl);
    const html = await response.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const articles = extractArticleData(doc);
    if (articles.length > 0) {
      return {
        ...articles[0],
        content: url  // Use the original URL for iframe src
      };
    }
    return null;
  } catch (err) {
    console.error(`Error fetching article from ${url}:`, err);
    return null;
  }
}

/**
 * Fetches announcements from the API and extracts article data from each HTML path
 *
 * @param {string} locale - Language locale ('en', 'zh_HK', 'zh_CN'). Defaults to 'en'
 * @param {string} tag - Tag to filter announcements by. If empty string (default), returns all articles
 * @returns {Promise<Array<{title: string, content: string, order: number, startTime: number, endTime: number}>>}
 */
export async function fetchAndExtractArticleData(locale = 'en', tag = '') {

  sendToUnity("get announcement");
  // Fetch the announcement list from API
  const response = await fetch(ANNOUNCEMENT_API_URL);
  const announcements = await response.json();

  const now = Math.floor(Date.now() / 1000);

  // Filter valid announcements and sort by order
  const validAnnouncements = announcements
    .filter(item => {
      const path = item.path[locale] || item.path.en;
      // Must have a valid path and be within time range
      const isValidPath = path && path.startsWith('http') && item.startTime <= now && item.endTime >= now;
      
      // If tag is specified, check if announcement's tags include it
      const matchesTag = tag === '' || (item.tags && item.tags.includes(tag));
      
      return isValidPath && matchesTag;
    })
    .sort((a, b) => a.order - b.order);

  // Fetch article data from each HTML path
  const results = await Promise.all(
    validAnnouncements.map(async (item) => {
      const path = item.path[locale] || item.path.en;
      const articleData = await fetchSingleArticle(path);

      if (articleData) {
        return {
          ...articleData,
          order: item.order,
          startTime: item.startTime,
          endTime: item.endTime
        };
      }
      return null;
    })
  );

  // Filter out failed fetches
  return results.filter(item => item !== null);
}

/**
 * Extracts article data from an HTML string
 *
 * @param {string} htmlString - HTML content as string
 * @returns {Array<{title: string}>}
 */
export function extractArticleDataFromString(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');

  return extractArticleData(doc);
}

export { ANNOUNCEMENT_API_URL };
export default fetchAndExtractArticleData;

function sendToUnity(message) {
  if (window.Unity && window.Unity.call) {
    // Android / Editor
    window.Unity.call(message);
  } else if (
    window.webkit &&
    window.webkit.messageHandlers &&
    window.webkit.messageHandlers.unityControl
  ) {
    // iOS
    window.webkit.messageHandlers.unityControl.postMessage(message);
  }
}
